import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  formatAddressMultiline,
  hasRequiredAddressFields,
} from "@plasma/utils/format-address";
import { applyPhoneNumberMask } from "@plasma/utils/phone-numbers";
import { Link } from "@tanstack/react-router";
import { MapPin, Phone } from "lucide-react";
import { twJoin } from "tailwind-merge";

import type { LocationItem } from "./locations-list";

interface LocationCardProps {
  active?: boolean;
  location: LocationItem;
  opacity?: string;
}

export function LocationCard({ active, location, opacity }: LocationCardProps) {
  const { attributes, listeners, setNodeRef, transform } = useSortable({
    id: location.id,
  });

  return (
    <Link
      className={twJoin(
        "flex h-56 w-full max-w-64 flex-col overflow-hidden rounded-lg border border-border bg-card shadow-xs transition-[color,box-shadow] hover:shadow-md focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring/50",
        opacity,
        active ? "hover:cursor-grabbing" : ""
      )}
      id={location.id}
      key={location.id}
      params={{ id: location.id }}
      ref={setNodeRef}
      style={{
        transform: CSS.Translate.toString(transform),
      }}
      to="/locations/$id/profile"
      {...attributes}
      {...listeners}
    >
      <div
        className={twJoin(
          "flex items-center gap-3 p-4",
          {
            inactive: "bg-muted",
            "fully-online": "bg-success/10",
            "partially-online": "bg-warning/10",
            offline: "bg-destructive/10",
          }[location.posStatus]
        )}
      >
        <img
          alt={`${location.name} logo`}
          className="size-9 rounded-full"
          height={36}
          src={location.logoUrl ?? "/images/location-logo-placeholder.svg"}
          width={36}
        />
        <div className="space-y-1 overflow-hidden">
          <p className="title text-foreground truncate">{location.name}</p>
          <div
            className={twJoin(
              "caption inline-flex items-center gap-1 rounded-full px-2 py-1",
              {
                inactive: "bg-muted text-foreground",
                "fully-online": "bg-success/10 text-success",
                "partially-online": "bg-warning/10 text-warning",
                offline: "bg-destructive/10 text-destructive",
              }[location.posStatus]
            )}
          >
            <div
              className={twJoin(
                "size-1.5 rounded-full",
                {
                  inactive: "bg-muted",
                  "fully-online": "bg-success",
                  "partially-online": "bg-warning",
                  offline: "bg-destructive",
                }[location.posStatus]
              )}
            />{" "}
            POS{" "}
            {
              {
                inactive: "Inactive",
                "fully-online": "Fully Online",
                "partially-online": "Partially Online",
                offline: "Offline",
              }[location.posStatus]
            }
          </div>
        </div>
      </div>
      <div className="space-y-2 p-4">
        {location.phoneNumber && (
          <div className="flex gap-1.5">
            <div className="p-0.5">
              <Phone className="size-3.5" />
            </div>
            <p className="caption text-muted-foreground">
              {applyPhoneNumberMask(location.phoneNumber)}
            </p>
          </div>
        )}
        <div className="flex gap-1.5">
          <div className="p-0.5">
            <MapPin className="size-3.5" />
          </div>
          <div>
            {hasRequiredAddressFields(location) ? (
              formatAddressMultiline(location).map((line) => (
                <p className="caption text-muted-foreground" key={line}>
                  {line}
                </p>
              ))
            ) : (
              <p className="caption text-muted-foreground">Address not set</p>
            )}
          </div>
        </div>
      </div>
    </Link>
  );
}
